import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Button,
    Stack,
    Text,
    Input,
    Box,
    Flex,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import WebDrawer from '../web-drawer'

const BATCH_LIMIT = 30

// Inline checkbox component following the design system palette
const Checkbox = ({checked, onChange, label, subLabel, styles}) => (
    <Flex
        as="label"
        sx={styles.checkboxLabel}
    >
        <input
            type="checkbox"
            checked={checked}
            onChange={onChange}
            style={{position: 'absolute', opacity: 0, width: 0, height: 0}}
        />
        {/* Custom checkbox box */}
        <Flex
            sx={{
                ...styles.checkboxIcon,
                borderColor: checked ? 'black' : 'webTheme.strokeSecondary',
                bg: checked ? 'black' : 'transparent'
            }}
        >
            {checked && (
                <svg width="11" height="8" viewBox="0 0 11 8" fill="none">
                    <path d="M1 3.5L4 6.5L10 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )}
        </Flex>
        <Box flex="1">
            <Text textStyle="bodySmRegular" noOfLines={1}>
                {label}
                {subLabel && (
                        <Text as="span" textStyle="bodySmRegular" ml={1}>
                        {subLabel}
                    </Text>
                )}
            </Text>
        </Box>
    </Flex>
)

// Chevron icon
const ChevronIcon = ({isOpen}) => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
        }}
    >
        <path d="M3 6L8 11L13 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

// X icon
const CloseIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
)

/**
 * Custom multi-select dropdown following the design system.
 *
 * CLOSED (no selection): placeholder + chevron
 * CLOSED (with selection): floated label + "Options selected (X)" + clear X + chevron
 * OPEN: header row + inline create field + checkbox list + footer Apply/Cancel
 */
const CollectionDropdown = ({collections, selectedIds, onToggle, onClear, onApply, onCancel, onOpenCreateModal}) => {
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('WishlistMoveItemsDrawer', {})
    const [isOpen, setIsOpen] = useState(false)
    const [newName, setNewName] = useState('')
    const dropdownRef = useRef(null)
    const hasSelection = selectedIds.length > 0

    // Close on outside click
    useEffect(() => {
        const handleOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleOutside)
        return () => document.removeEventListener('mousedown', handleOutside)
    }, [])

    const handleCreate = () => {
        setIsOpen(false)
        onOpenCreateModal(newName.trim(), (createdFolder) => {
            if (createdFolder?.id) {
                onToggle(createdFolder.id)
                setNewName('')
            }
        })
    }

    const handleApply = () => {
        setIsOpen(false)
        onApply()
    }

    const handleCancel = () => {
        setIsOpen(false)
        onCancel()
    }

    const label = formatMessage({id: 'wishlist.move_items.dropdown_label', defaultMessage: 'Select Wishlist Collection'})
    const labelRequired = `${label} *`

    return (
        <Box ref={dropdownRef} position="relative" w="100%">
            {/* Trigger */}
            <Flex
                sx={styles.dropdownTrigger}
                onClick={() => setIsOpen((o) => !o)}
            >
                <Box flex="1" overflow="hidden">
                    {hasSelection ? (
                        <Box>
                            <Text sx={styles.dropdownSelectedLabel}>
                                {label}
                            </Text>
                            <Text textStyle="bodySmRegular" noOfLines={1}>
                                <FormattedMessage
                                    id="wishlist.move_items.options_selected"
                                    defaultMessage="Options selected ({count})"
                                    values={{count: selectedIds.length}}
                                />
                            </Text>
                        </Box>
                    ) : (
                        <Text textStyle="bodySmRegular" color="webTypo.textTertiary">{labelRequired}</Text>
                    )}
                </Box>
                <Flex align="center" gap={2} ml={2} flexShrink={0}>
                    {hasSelection && (
                        <Box
                            as="span"
                            color="webTypo.textSecondary"
                            p={1}
                            onClick={(e) => {
                                e.stopPropagation()
                                onClear()
                            }}
                            _hover={{color: 'black'}}
                            aria-label="Clear selection"
                        >
                            <CloseIcon />
                        </Box>
                    )}
                    <Box color="webTypo.textSecondary">
                        <ChevronIcon isOpen={isOpen} />
                    </Box>
                </Flex>
            </Flex>

            {/* Dropdown panel */}
            {isOpen && (
                <Box sx={styles.dropdownPanel}>
                    {/* Inline create field */}
                    <Box sx={styles.dropdownCreateContainer}>
                        <Flex sx={styles.dropdownCreateInner}>
                        <Input
                            flex="1"
                            variant="outline"
                            placeholder={formatMessage({
                                id: 'wishlist.move_items.new_collection_placeholder',
                                defaultMessage: 'New Collection'
                            })}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                            border="none"
                            borderRadius={0}
                            px={0}
                            h="32px"
                            fontSize="sm"
                            _focusVisible={{boxShadow: 'none'}}
                        />
                        <Button
                            type="button"
                            variant="unstyled"
                            onClick={handleCreate}
                            sx={styles.dropdownCreateButton}
                        >
                            <FormattedMessage id="wishlist.move_items.create_btn" defaultMessage="Create" />
                        </Button>
                        </Flex>
                    </Box>

                    {/* Collection list */}
                    <Box sx={styles.dropdownList}>
                        {collections.length > 0 ? (
                            collections.map((folder) => (
                                <Checkbox
                                    key={folder.id}
                                    checked={selectedIds.includes(folder.id)}
                                    onChange={() => onToggle(folder.id)}
                                    label={folder.name}
                                    styles={styles}
                                    subLabel={
                                        folder.customerProductListItems?.length > 0
                                            ? `(${folder.customerProductListItems.length})`
                                            : undefined
                                    }
                                />
                            ))
                        ) : (
                            <Text textStyle="bodySmRegular" color="webTypo.textSecondary" py={3}>
                                <FormattedMessage
                                    id="wishlist.move_items.no_collections"
                                    defaultMessage="No collections available. Create one above."
                                />
                            </Text>
                        )}
                    </Box>

                    {/* Footer Apply / Cancel */}
                    <Flex sx={styles.dropdownFooter}>
                        <Button
                            type="button"
                            variant="webPrimary"
                            sx={styles.dropdownFooterButton}
                            isDisabled={selectedIds.length === 0}
                            onClick={handleApply}
                        >
                            <FormattedMessage id="wishlist.move_items.apply_btn" defaultMessage="Apply" />
                        </Button>
                        <Button
                            type="button"
                            variant="webSecondaryOutline"
                            sx={styles.dropdownFooterButton}
                            onClick={handleCancel}
                        >
                            <FormattedMessage id="wishlist.folder.cancel" defaultMessage="Cancel" />
                        </Button>
                    </Flex>
                </Box>
            )}
        </Box>
    )
}

/**
 * Drawer for moving selected items to one or more collections.
 */
const WishlistMoveItemsDrawer = ({
    isOpen,
    onClose,
    selectedCount,
    collections,
    onApply,
    onOpenCreateModal,
    isFromFavourites
}) => {
    const {formatMessage} = useIntl()
    const [selectedIds, setSelectedIds] = useState([])

    // Reset on open
    useEffect(() => {
        if (isOpen) setSelectedIds([])
    }, [isOpen])

    const toggleFolder = (id) =>
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        )

    const handleApply = () => {
        if (selectedIds.length === 0) return
        onApply({targetIds: selectedIds})
        onClose()
    }

    // Moving FROM "My Favourite" is really an ADD (the item stays in Favourites), so the copy
    // must say "add". Moving FROM a custom folder is a true move between collections.
    const title = isFromFavourites
        ? formatMessage({id: 'wishlist.move_items.title_add', defaultMessage: 'Add to collection'})
        : formatMessage({id: 'wishlist.move_items.title', defaultMessage: 'Move Items'})

    return (
        <WebDrawer isOpen={isOpen} onClose={onClose} title={title} size="full">
            <Stack spacing={6}>
                {/* Subheader */}
                <Text textStyle="bodySmRegular">
                    {selectedCount > 1 ? (
                        <>
                            {isFromFavourites ? (
                                <FormattedMessage
                                    id="wishlist.move_items.subheader_prefix_add"
                                    defaultMessage="Select a collection to add these"
                                />
                            ) : (
                                <FormattedMessage
                                    id="wishlist.move_items.subheader_prefix"
                                    defaultMessage="Select a collection to move these"
                                />
                            )}
                            {' '}
                            <Text as="span" textStyle="bodySmBold">
                                <FormattedMessage
                                    id="wishlist.move_items.subheader_count"
                                    defaultMessage="{count} items"
                                    values={{count: selectedCount}}
                                />
                            </Text>
                            {' '}
                            <FormattedMessage
                                id="wishlist.move_items.subheader_suffix"
                                defaultMessage="to:"
                            />
                        </>
                    ) : isFromFavourites ? (
                        <FormattedMessage
                            id="wishlist.move_items.subheader_single_add"
                            defaultMessage="Select a collection to add this item to:"
                        />
                    ) : (
                        <FormattedMessage
                            id="wishlist.move_items.subheader_single"
                            defaultMessage="Select a collection to move this item to:"
                        />
                    )}
                </Text>

                {/* Custom multi-select dropdown */}
                <CollectionDropdown
                    collections={collections}
                    selectedIds={selectedIds}
                    onToggle={toggleFolder}
                    onClear={() => setSelectedIds([])}
                    onApply={handleApply}
                    onCancel={onClose}
                    onOpenCreateModal={onOpenCreateModal}
                />

                {/* Batch limit warning */}
                {selectedCount > BATCH_LIMIT && (
                    <Text textStyle="bodySmRegular" color="webTypo.textError">
                        <FormattedMessage
                            id="wishlist.move_items.batch_limit_warning"
                            defaultMessage="Only the first {limit} items will be moved per request."
                            values={{limit: BATCH_LIMIT}}
                        />
                    </Text>
                )}

                {/* Bottom Apply / Cancel — visible when dropdown is closed with a selection */}
                {selectedIds.length > 0 && (
                    <Stack spacing={3}>
                        <Button type="button" variant="webPrimary" w="100%" onClick={handleApply}>
                            <FormattedMessage id="wishlist.move_items.confirm_btn" defaultMessage="Confirm" />
                        </Button>
                        <Button type="button" variant="webSecondaryOutline" w="100%" onClick={onClose}>
                            <FormattedMessage id="wishlist.folder.cancel" defaultMessage="Cancel" />
                        </Button>
                    </Stack>
                )}
            </Stack>
        </WebDrawer>
    )
}

WishlistMoveItemsDrawer.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    selectedCount: PropTypes.number,
    collections: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string, name: PropTypes.string})),
    onApply: PropTypes.func.isRequired,
    onOpenCreateModal: PropTypes.func.isRequired,
    isFromFavourites: PropTypes.bool
}

WishlistMoveItemsDrawer.defaultProps = {
    selectedCount: 1,
    collections: [],
    isFromFavourites: false
}

export default WishlistMoveItemsDrawer
